import React, { FC, useState } from 'react';
import { AxiosError } from "axios";
import ReactPaginate from "react-paginate";
import { classNames } from "primereact/utils";
import { Dropdown } from "primereact/dropdown";
import { useMutation } from "@tanstack/react-query";

import { useToast } from "../../../hooks/useToast";
import { requests } from "../../../services/http-common";
import { perPageOptions } from "../../../config/config";
import { TReactQueryReFetch } from "../../../@types/global";
import ButtonPrimary from "../../../uiComponents/ButtonPrimary/ButtonPrimary";
import sharedStyles from "../../../assets/sharedStyles.module.scss";
import { extractErrorMessage } from "../../../helpers/extractErrorMessage";
import ConfirmDialogCustom from "../../../uiComponents/ConfirmDianlogCustom/ConfirmDialogCustom";

import AddRowManually from "../BulkUploadUsers/AddRowManually/AddRowManually";

import TableBody from "./Table/TableBody";
import TableHead from "./Table/TableHead";
import styles from './draftUsers.module.scss';

const labelObj = {
    header: `Deleting all draft records`,
    message: 'You are about to delete all draft records.  Do you wish to continue?',
    successLabel: 'Success delete',
    confirmLabel: 'Delete',
}

export type TDraftUser = Record<string, string | number>
interface TDraftUsersProps {
    draftUsersList: TDraftUser[];
    page: number;
    size: number;
    currentLength: number;
    totalElements: number;
    changePage: (val: number) => void;
    pageCount: number;
    changePerPage: (val: number) => void;
    columnHeaders: { key: string; label: string }[];
    companyId: number;
    refetchDraftUsers: TReactQueryReFetch;
}
const DraftUsers: FC<TDraftUsersProps> = ({
    draftUsersList,
    page,
    size,
    currentLength,
    changePage,
    pageCount,
    changePerPage,
    columnHeaders,
    companyId,
    totalElements,
    refetchDraftUsers,
}) => {
    const { show } = useToast();
    const [isTableLoading, setTableLoading] = useState<boolean>(false);
    const [isClearDraftWarningModalOpened, setClearDraftModal] = useState(false);
    const [isPublishWarningModalOpened, setPublishModal] = useState(false);
    const [processedCount, setProcessedCount] = useState<number>(0);
    const [deleteUserId, setDeleteUserId] = useState<number | null>(null);
    const [publishData, setPublishData] = useState<Record<string, number>>({
        processedCount: 0,
        remainingDraftCount: totalElements,
    });


    const onPublishDraftUsers = useMutation({
        mutationFn: async () => {
            return await requests.post(
                `api/companies/${companyId}/file83b-draft/publish`, {}
            );
        },
        onSuccess: (data) => {
            const { processedCount, remainingDraftCount } = data;
            setPublishData({ processedCount, remainingDraftCount });
            setProcessedCount(prevState => prevState + processedCount);
            show({
                severity: 'success',
                detail: `${processedCount} records successfully published`,
            });
            refetchDraftUsers().then(() => {
                setTableLoading(false);
            });
        },
        onError: (error: AxiosError<{ detail: string }>) => {
            const errorData = {
                error: error,
                show: show,
                cb: () => {
                    setTableLoading(false);
                }
            };
            extractErrorMessage(errorData);
        },
    });
    const { isPending: publishingLoading } = onPublishDraftUsers;
    const onClearDraftUsers = useMutation({
        mutationFn: async () => {
            return await requests.delete(
                `api/companies/${companyId}/file83b-draft/all`
            );
        },
        onSuccess: () => {
            refetchDraftUsers().then(() => {
                setTableLoading(false);
            });
        },
        onError: (error: AxiosError<{ detail: string }>) => {
            const errorData = {
                error: error,
                show: show,
                cb: () => {
                    setTableLoading(false);
                }
            };
            extractErrorMessage(errorData);
        },
    });
    const deleteUserRequest = useMutation({
        mutationFn: async (rowId: number) => {
            return await requests.delete(
                `api/companies/${companyId}/file83b-draft?ids=${rowId}`,
            );
        },
        onSuccess: () => {
            if (page > 0 && draftUsersList.length === 1) {
                const onePageBack = page - 1;
                changePage(onePageBack);
            } else {
                refetchDraftUsers().then((res) => {
                    setPublishData(prev => {
                        return {
                            ...prev,
                            remainingDraftCount: res.data.totalElements
                        }
                    });
                    setDeleteUserId(null);
                    setTableLoading(false);
                });
            }
        },
        onError: (error: AxiosError<{ detail: string }>, variables) => {
            const errorData = {
                error: error,
                variables: variables,
                retryFn: deleteUserRequest.mutate,
                show: show,
                cb: () => {
                    setDeleteUserId(null);
                }
            };
            extractErrorMessage(errorData);
        },
    });

    const onDeleteSingleUser = (rowId: number) => {
        deleteUserRequest.mutate(rowId);
    }
    const onAcceptPublish = () => {
        setTableLoading(true);
        onPublishDraftUsers.mutate();
    }
    const onPublish = () => {
        setPublishModal(true);
    }
    const onClear = () => {
        setTableLoading(true);
        onClearDraftUsers.mutate();
    }
    const manuallyRowAdditionSuccessCb = () => {
        if (draftUsersList.length === size) {
            const nextPage = page + 1;
            changePage(nextPage);
        }
    }

    return (
        <div className="bg-white h-full w-full flex flex-column p-4 border-top-1 border-200">
            <div className="flex justify-content-end gap-3 pt-2 pb-4">
                <AddRowManually
                    columnHeaders={columnHeaders}
                    companyId={companyId}
                    refetchDraftUsers={refetchDraftUsers}
                    successCb={manuallyRowAdditionSuccessCb}
                />

                <ButtonPrimary
                    label="Clear draft data"
                    styleType="primaryOutline"
                    onClick={() => setClearDraftModal(true)}
                    isDanger
                />
                <ButtonPrimary
                    label="Publish"
                    onClick={onPublish}
                    disabled={publishingLoading}
                />
            </div>
            <div className="w-full overflow-auto" style={{ height: 'calc(100% - 150px)' }}>
                <div className={styles.draftUsers}>
                    <div className={styles.tableWrapper}>

                        <table className={classNames(styles.table, {
                            [styles.loading]: isTableLoading || publishingLoading,
                        })}>
                            <TableHead columnsConfig={columnHeaders} />
                            <TableBody
                                draftUsersList={draftUsersList}
                                columnsConfig={columnHeaders}
                                companyId={companyId}
                                refetchDraftUsers={refetchDraftUsers}
                                setTableLoading={setTableLoading}
                                deleteUserId={deleteUserId}
                                setDeleteUserId={setDeleteUserId}
                                onDeleteSingleUser={onDeleteSingleUser}
                            />
                        </table>
                    </div>
                </div>
            </div>
            <div className="py-2 flex justify-content-between align-items-center aligin-self-end mt-auto">
                <div className="gap-2">
                    {`Showing ${page * size + 1} to ${page * size + currentLength
                        } of ${totalElements} entries`}
                </div>
                <div className="flex gap-2">
                    <ReactPaginate
                        breakLabel="..."
                        forcePage={page}
                        pageCount={pageCount}
                        pageRangeDisplayed={9}
                        marginPagesDisplayed={1}
                        className={sharedStyles.paginationWrapper}
                        activeClassName={sharedStyles.pItemActive}
                        nextLabel={<div className={sharedStyles.pItem}>{`>`}</div>}
                        onPageChange={({ selected }) => changePage(selected)}
                        previousLabel={<div className={sharedStyles.pItem}>{`<`}</div>}
                        renderOnZeroPageCount={null}
                        pageLabelBuilder={(page) => (
                            <div className={sharedStyles.pItem}>{page}</div>
                        )}
                    />
                    <Dropdown
                        options={perPageOptions}
                        value={size}
                        onChange={(e) => changePerPage(e.value)}
                    />
                </div>
            </div>
            <ConfirmDialogCustom
                header={labelObj.header}
                acceptLabel={labelObj.confirmLabel}
                rejectLabel="Cancel"
                message={labelObj.message}
                visible={isClearDraftWarningModalOpened}
                onHide={() => setClearDraftModal(false)}
                accept={onClear}
            />
            <ConfirmDialogCustom
                rejectLabel="Cancel"
                accept={onAcceptPublish}
                header="Publish draft elections"
                isLoading={publishingLoading}
                visible={isPublishWarningModalOpened}
                acceptLabel={publishData.remainingDraftCount === 0 ? "Close" : "Proceed"}
                shouldHideOnAccept={publishData.remainingDraftCount === 0}
                onHide={() => setPublishModal(false)}
                message={
                    <div className="flex flex-column gap-3">
                        <div className="p-confirm-dialog-message">
                            Draft 83(b) elections will be created based on your provided information,
                            and users will get a notification to review and complete their documents.
                            Are you sure you want to proceed?
                        </div>
                        <div className="flex flex-column">
                            <div className="text-800">
                                <span className="mr-1">Published:</span>
                                <span className="font-bold text-900">{processedCount}</span>
                                <span className="ml-1">records</span>
                            </div>
                            <div className="text-800">
                                <span className="mr-1">Remaining:</span>
                                <span className="font-bold text-900">{publishData.remainingDraftCount}</span>
                                <span className="ml-1">records</span>
                            </div>
                        </div>
                    </div>
                }

            />
        </div>
    )
}

export default DraftUsers;
